import { Type } from '@angular/core';
import { CustomPermissionKey } from '../models/user';
import { DashboardComponent } from './dashboard/dashboard.component';
import { UserActionLogComponent } from './user-action-log/user-action-log.component';
import { ReferalLogComponent } from './referal-log/referal-log.component';
import { ArrayObjectEditorComponent } from '../shared-modules/array-object-editor/array-object-editor.component';
import { MoveMetaToExtraComponent } from './move-meta-to-extra/move-meta-to-extra.component';
import { FacilitiesSettingsComponent } from './facilities-settings/facilities-settings.component';
import { BedBoardSettingsComponent } from './bedboard-settings/bedboard-settings.component';
import { ActionCodesSettingsComponent } from './action-codes-settings/action-codes-settings.component';
import { FacilityConfigComponent } from './facility-config/facility-config.component';
import { EhrSourcesSettingsComponent } from './ehr-sources-settings/ehr-sources-settings.component';
import { AiDecisionLogicComponent } from './ai-decision-logic/ai-decision-logic.component';
import { DeclineReasonMappingComponent } from './decline-reason-mapping/decline-reason-mapping.component';
import { BedSetupSettingsComponent } from './bed-setup-settings/bed-setup-settings.component';
import { ApiKeysComponent } from './api-keys/api-keys.component';
import { AdvancedDirectiveSettingsComponent } from './advanced-directive-settings/advanced-directive-settings.component';
import { ScheduledEmailsComponent } from './scheduled-emails/scheduled-emails.component';
import { InternalDeclineReasonsComponent } from './internal-decline-reasons/internal-decline-reasons.component';
import { ClientSettingsComponent } from './client-settings/client-settings.component';
import { CensusActionLabelsComponent } from './census-action-labels/census-action-labels.component';
import { CadaraSettingsComponent } from './cadara-settings/cadara-settings.component';
import { FacilityAssignmentComponent } from './facility-assignment/facility-assignment.component';
import { FaxToEmailComponent } from './fax-to-email/fax-to-email.component';
import { FacilityGroupsComponent } from './facility-groups/facility-groups.component';
import { DischargeAccuracyComponent } from './discharge-accuracy/discharge-accuracy.component';
import { ROLE_NUMBER_CI_MANAGER, ROLE_NUMBER_CLINICAL_MANAGER, ROLE_NUMBER_GLOBAL_ADMIN, isSnfAdmin, isSuperAdmin } from 'list-types-public';
import { isScheduledEmailsEnabledForClient } from './scheduled-emails/scheduled-emails-entitlement';
import { isDischargePlanEnabledForClient } from '../manageb/discharge-plan/discharge-plan-entitlement';

/**
 * Client switches on Client Settings that show or hide an admin page. A page
 * that carries one is listed and reachable only while the flag is on - the
 * client axis, on top of the role axis in allowedRoles. Read through the
 * settings cache (see the entitlement file).
 */
export type ClientFeatureFlag = 'scheduledEmails' | 'dischargePlan';

export function hasClientFlag(flag: ClientFeatureFlag): boolean {
  switch (flag) {
    case 'scheduledEmails':
      return isScheduledEmailsEnabledForClient();
    case 'dischargePlan':
      return isDischargePlanEnabledForClient();
  }
}

// snfAiAdmin is SNF AI's own user inside each client (177): every fullAdmin page
// plus the client switches. superAdmin is a superset of everything.
export type AdminNavRole = 'fullAdmin' | 'regionAdmin' | 'snfAiAdmin' | 'superAdmin';
export const ROLES_FULL: AdminNavRole[] = ['fullAdmin', 'snfAiAdmin', 'superAdmin'];
export const ROLES_FULL_AND_REGION: AdminNavRole[] = ['fullAdmin', 'regionAdmin', 'snfAiAdmin', 'superAdmin'];
// Pages the client's own SNF_ADMIN must not reach - the nav hides them rather
// than letting the user walk into a 403.
export const ROLES_SUPER: AdminNavRole[] = ['superAdmin'];
// The client switches (Client Settings): SNF AI's user (177) and super admins,
// the same predicate as the server's requireClientSettingsAdmin - so the menu
// can never offer the page to a full admin the API refuses.
export const ROLES_SNFAI: AdminNavRole[] = ['snfAiAdmin', 'superAdmin'];
// Discharge Analytics: the limited admins (145, 152, 190), SNF AI's user (177)
// and the super admins - the roles the server's requireDischargePlanAccuracyAdmin admits.
export const ROLES_DISCHARGE_ACCURACY: AdminNavRole[] = ['regionAdmin', 'snfAiAdmin', 'superAdmin'];

// Clinical Manager (145) and Global Admin (190) get the same limited page set as the region admin (152).
const LIMITED_ADMIN_ROLE_IDS: readonly number[] = [ROLE_NUMBER_CI_MANAGER, ROLE_NUMBER_CLINICAL_MANAGER, ROLE_NUMBER_GLOBAL_ADMIN];

export function resolveAdminNavRole(roleNumber?: number | null): AdminNavRole {
  const role = Number(roleNumber);
  if (!Number.isFinite(role)) return 'fullAdmin';
  // isSuperAdmin / isSnfAdmin are the same predicates the server gates these
  // endpoints with, so the menu can never offer a page the API will refuse.
  if (isSuperAdmin(role)) return 'superAdmin';
  if (isSnfAdmin(role)) return 'snfAiAdmin';
  return LIMITED_ADMIN_ROLE_IDS.includes(role) ? 'regionAdmin' : 'fullAdmin';
}

const ADMIN_BASE_PATH = '/manageb/admin';

export interface AdminRouteConfigItem {
  path: string;
  title?: string;
  allowedRoles: AdminNavRole[];
  // one of component (eager) or loadChildren (lazy, own bundle) must be set
  component?: Type<unknown>;
  loadChildren?: () => Promise<Type<unknown>>;
  requiredPermission?: CustomPermissionKey;
  requiredClientFlag?: ClientFeatureFlag;
}

export const ADMIN_ROUTES_CONFIG: AdminRouteConfigItem[] = [
  // No title: the Admin tile already lands on the dashboard, so the menu must not repeat it.
  { path: 'dashboard', allowedRoles: ROLES_FULL, component: DashboardComponent },
  { path: 'userLog', title: 'User Log', allowedRoles: ROLES_FULL, component: UserActionLogComponent },
  { path: 'referalLog', title: 'Referal Log', allowedRoles: ROLES_FULL, component: ReferalLogComponent },
  { path: 'pcc', allowedRoles: ROLES_FULL, component: ArrayObjectEditorComponent },
  { path: 'temp', allowedRoles: ROLES_FULL, component: MoveMetaToExtraComponent },
  { path: 'facilities-settings', title: 'Facilities Settings', allowedRoles: ROLES_FULL, component: FacilitiesSettingsComponent },
  { path: 'facilities-config', allowedRoles: ROLES_FULL, component: FacilityConfigComponent },
  { path: 'report-settings', title: 'Report Settings', allowedRoles: ROLES_FULL, component: BedBoardSettingsComponent },
  // Listed. It was unlisted (title commented out) while the page waited on sign-off;
  // commenting the title out again is what hides it from both admin menus. Either way
  // the route and page stay registered, so a direct /manageb/admin/scheduled-emails
  // URL works regardless of whether the entry is listed.
  { path: 'scheduled-emails', title: 'Scheduled Emails', allowedRoles: ROLES_FULL_AND_REGION, component: ScheduledEmailsComponent, requiredClientFlag: 'scheduledEmails' },
  { path: 'action-codes-settings', allowedRoles: ROLES_FULL, component: ActionCodesSettingsComponent },
  { path: 'ehr-sources-settings', title: 'EHR Sources Settings', allowedRoles: ROLES_FULL, component: EhrSourcesSettingsComponent },
  { path: 'decline-reason-mapping', title: 'Decline Reason Mapping', allowedRoles: ROLES_FULL_AND_REGION, component: DeclineReasonMappingComponent },
  { path: 'ai-decision-logic', title: 'AI Decision Logic', allowedRoles: ROLES_FULL_AND_REGION, component: AiDecisionLogicComponent },
  { path: 'bed-setup', title: 'Bed Setup', allowedRoles: ROLES_FULL, component: BedSetupSettingsComponent },
  { path: 'api-keys', title: 'API Keys', allowedRoles: ROLES_FULL, component: ApiKeysComponent },
  { path: 'advanced-directive-settings', title: 'Advanced Directive Settings', allowedRoles: ROLES_FULL_AND_REGION, component: AdvancedDirectiveSettingsComponent },
  { path: 'internal-decline-reasons', title: 'Internal Decline Reasons', allowedRoles: ROLES_FULL_AND_REGION, component: InternalDeclineReasonsComponent },
  { path: 'client-settings', title: 'Client Settings', allowedRoles: ROLES_SNFAI, component: ClientSettingsComponent },
  { path: 'discharge-accuracy', title: 'Discharge Analytics', allowedRoles: ROLES_DISCHARGE_ACCURACY, component: DischargeAccuracyComponent, requiredClientFlag: 'dischargePlan' },
  { path: 'census-action-labels', title: 'Census Action Labels', allowedRoles: ROLES_FULL_AND_REGION, component: CensusActionLabelsComponent },
  // Same roles the server's Cadara routes admit: SNF AI's admin (177) and super admins.
  { path: 'cadara-settings', title: 'Cadara Settings', allowedRoles: ROLES_SNFAI, component: CadaraSettingsComponent },
  { path: 'facility-assignment', title: 'Facility Assignment', allowedRoles: ROLES_SNFAI, component: FacilityAssignmentComponent },
  { path: 'facility-groups', title: 'Facility Groups', allowedRoles: ROLES_FULL_AND_REGION, component: FacilityGroupsComponent },
  { path: 'fax-to-email', title: 'Fax to Email', allowedRoles: ROLES_FULL, component: FaxToEmailComponent },
  { path: 'user-credentials', title: 'User Credentials', allowedRoles: ROLES_FULL, loadChildren: () => import('./user-credentials/user-credentials.module').then((m) => m.UserCredentialsModule), requiredPermission: 'userCredentials' },
  { path: 'ehr-credentials', title: 'EHR Configurations', allowedRoles: ROLES_FULL, loadChildren: () => import('./ehr-credentials/ehr-credentials.module').then((m) => m.EhrCredentialsModule), requiredPermission: 'ehrCredentials' },
];

export interface AdminNavEntry {
  title: string;
  path: string;
  roles: AdminNavRole[];
  requiredPermission?: CustomPermissionKey;
  requiredClientFlag?: ClientFeatureFlag;
}

export function getAdminNavItems(): AdminNavEntry[] {
  return ADMIN_ROUTES_CONFIG
    .filter((item): item is AdminRouteConfigItem & { title: string } => !!item.title)
    .map((item) => ({
      title: item.title,
      path: `${ADMIN_BASE_PATH}/${item.path}`,
      roles: item.allowedRoles,
      requiredPermission: item.requiredPermission,
      requiredClientFlag: item.requiredClientFlag,
    }));
}

/** The role, permission and client-flag gates every admin menu applies, in one place. */
export function isAdminNavEntryVisible(
  entry: AdminNavEntry,
  role: AdminNavRole,
  hasPermission: (permission: CustomPermissionKey) => boolean,
): boolean {
  if (!entry.roles.includes(role)) { return false; }
  if (entry.requiredPermission && !hasPermission(entry.requiredPermission)) { return false; }
  if (entry.requiredClientFlag && !hasClientFlag(entry.requiredClientFlag)) { return false; }
  return true;
}
